import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import { Sailboat, Ship, UserPlus, Users, Wallet } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'react-hot-toast'
import { useNavigate } from 'react-router'

import type { Organization, Role } from '@ankor-io/common/types/account'
import { Button } from '@ankor-io/ui-components/src/Button'
import { Heading, Subheading } from '@ankor-io/ui-components/src/Heading'
import { Input } from '@ankor-io/ui-components/src/Input'

import { useCreateOrganizationInviteMutation } from '@/api/queries/useUsersQuery'
import { useModuleTheme } from '@/providers/moduleTheme'
import { useUserContext } from '@/providers/user/useUserContext'
import InviteUsersModal from '@/views/components/InviteUserModal'
import NewVesselModal from '@/views/components/NewVesselModal/NewVesselModal'

const WelcomePage: React.FC = () => {
  const { moduleTheme } = useModuleTheme()
  const { getAccessToken } = useKindeAuth()
  const navigate = useNavigate()

  const { organizations, activeOrganizationId, updateOrganization } = useUserContext()
  const organizationNameFromContext = activeOrganizationId ? organizations[activeOrganizationId]?.name || '' : ''
  const [organizationName, setOrganizationName] = useState(
    activeOrganizationId ? organizations[activeOrganizationId]?.name || '' : '',
  )
  const [isAccountModalOpen, setIsAccountModalOpen] = useState(false)
  const [isInviteUsersModalOpen, setIsInviteUsersModalOpen] = useState(false)

  const createInviteMutation = useCreateOrganizationInviteMutation()

  const handleOrganizationNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setOrganizationName(event.target.value)
  }

  const handleOrganizationNameSave = async () => {
    const organizationDetail = activeOrganizationId ? organizations[activeOrganizationId] : null
    if (!organizationDetail) {
      toast.error('Something went wrong. No organization found.')
      return
    }
    try {
      const organization: Organization = {
        id: organizationDetail.id,
        name: organizationName,
        description: organizationDetail.description,
        visibility: organizationDetail.visibility,
      }
      const res = await fetch(`/api/iam/org/${activeOrganizationId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${await getAccessToken()}`,
        },
        body: JSON.stringify(organization),
      })

      if (res.ok) {
        toast.success('Organization name saved')
        updateOrganization(organizationDetail.id, organizationName, organizationDetail.description)
      } else {
        toast.error('Failed to save organization name')
      }
    } catch (error) {
      console.error(error)
    }
  }

  const handleUserInvite = async (userData: { email: string; role: Role }) => {
    const invitePromise = async () => {
      const invite = await createInviteMutation.mutateAsync({
        email: userData.email,
        roles: [userData.role],
      })
      setIsInviteUsersModalOpen(false)
      return invite
    }
    toast.promise(invitePromise, {
      loading: 'Inviting user...',
      success: 'User invited successfully',
      error: 'Failed to invite user',
    })
  }

  // Update the organization name when the active organization changes or the organization name in the context changes
  useEffect(() => {
    setOrganizationName(activeOrganizationId ? organizations[activeOrganizationId]?.name || '' : '')
  }, [activeOrganizationId, organizations, organizationNameFromContext])

  return (
    <>
      <div className="flex items-center justify-center">
        <div className="max-w-4xl w-full space-y-8">
          {/* Welcome Header */}
          <div className="text-center space-y-4">
            <div className="flex justify-center">
              <div className="size-16 bg-gray-900 dark:bg-gray-100 rounded-full flex items-center justify-center">
                <Sailboat className="size-8 text-white dark:text-gray-900" />
              </div>
            </div>
            <Heading level={1}>Welcome to AnkorPay</Heading>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              Your comprehensive solution for managing vessel accounts, expenses, and crew finances.
            </p>
          </div>

          {/* Organization Name Section */}
          <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 shadow-xs p-6">
            <div className="max-w-md mx-auto space-y-4">
              <div className="text-center">
                <Subheading
                  level={3}
                  className="mb-2"
                >
                  Organization Name
                </Subheading>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Set up your organization name to get started
                </p>
              </div>
              <div className="flex justify-center gap-2">
                <Input
                  value={organizationName}
                  onChange={handleOrganizationNameChange}
                  placeholder="Enter your organization name"
                  className="text-center"
                />
                <Button
                  color={moduleTheme.button}
                  onClick={handleOrganizationNameSave}
                  disabled={!organizationName}
                >
                  Save
                </Button>
              </div>
            </div>
          </div>

          {/* Features Overview */}
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 shadow-xs p-6 text-center">
              <div className="mx-auto size-12 bg-gray-100 rounded-lg flex items-center justify-center mb-4">
                <Wallet className="size-6 text-gray-700" />
              </div>
              <Subheading
                level={3}
                className="mb-2"
              >
                Wallet Management
              </Subheading>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Track balances across multiple currencies and manage vessel finances
              </p>
            </div>

            <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 shadow-xs p-6 text-center">
              <div className="mx-auto size-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-4">
                <Sailboat className="size-6 text-emerald-700" />
              </div>
              <Subheading
                level={3}
                className="mb-2"
              >
                Vessels
              </Subheading>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Organize expenses and budgets by individual vessels
              </p>
            </div>

            <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 shadow-xs p-6 text-center">
              <div className="mx-auto size-12 bg-violet-100 rounded-lg flex items-center justify-center mb-4">
                <Users className="size-6 text-violet-700" />
              </div>
              <Subheading
                level={3}
                className="mb-2"
              >
                Team Management
              </Subheading>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Issue cards and manage crew member spending permissions
              </p>
            </div>
          </div>

          {/* Get Started Section */}
          <div className="bg-white dark:bg-gray-900 rounded-lg border-2 border-gray-200 dark:border-gray-800 shadow-xs">
            <div className="p-6 text-center space-y-4">
              <Subheading level={2}>Get Started</Subheading>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                Create your first vessel account to begin managing your vessel's finances, or invite team members to
                join you.
              </p>

              <div className="flex justify-center pt-4 space-x-4">
                <Button
                  color={moduleTheme.button}
                  onClick={() => setIsAccountModalOpen(true)}
                >
                  <Ship data-slot="icon" />
                  Create Your First Vessel
                </Button>
                <Button
                  color={moduleTheme.button}
                  onClick={() => setIsInviteUsersModalOpen(true)}
                >
                  <UserPlus data-slot="icon" />
                  Invite Team Members
                </Button>
              </div>

              <p className="text-sm text-gray-500 pt-2">
                You'll be able to add wallets, manage expenses, and issue cards once your vessel is set up.
              </p>
            </div>
          </div>

          {/* Support Section */}
          <div className="text-center text-sm text-gray-500">
            <p>Need help getting started? Contact our support team or check out our documentation.</p>
          </div>
        </div>
      </div>

      <NewVesselModal
        setIsOpen={setIsAccountModalOpen}
        isOpen={isAccountModalOpen}
        emitConnectedAccountCreated={() => {
          navigate('/kyc-rfi', { replace: true })
        }}
      />

      <InviteUsersModal
        isOpen={isInviteUsersModalOpen}
        onClose={() => setIsInviteUsersModalOpen(false)}
        onInvite={handleUserInvite}
        existingUsers={[]}
        existingInvites={[]}
      />
    </>
  )
}

export default WelcomePage
